import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import AntdProForm from '@ant-design/pro-form';
import PropTypes from 'prop-types';
import { message } from 'antd';
import fp from 'lodash/fp';
import {
  UPDATE_PROJECT_PROFILE,
  GET_PROJECTS,
  GET_PROJECT_PROFILE,
} from '../../../../gql/projects';
import {
  getId,
  convertFilesForInitialValues,
  formatDatePickerValue,
} from '../../../../utils';
import useLoading from '../../../../hooks/useLoading';
import ProForm from '../../../layout/ProForm';
import FundingForm from './FundingForm';
import GeneralForm from './GeneralForm';
import ScheduleForm from './ScheduleForm';
import OtherForm from './OtherForm';
import { GET_LATEST_TERMINATION } from 'gql/termination';

const getInitialValues = ({ data }) => {
  const { title, projectStatus, projectCategory, profile } =
    data.projects.nodes[0];

  return {
    title,
    projectCategory: projectCategory.id,
    projectType: profile?.projectType?.id,
    accountCode: profile?.accountCode,
    workProgramme: profile?.workProgramme,
    projectCode: profile?.projectCode,
    accountSetUpDate: profile?.accountSetUpDate,
    accountClosedDate: profile?.accountClosedDate,
    faculty: profile?.department.faculty.id,
    department: profile?.department.id,
    projectStatus: projectStatus?.id,
    researchRelated: profile?.researchRelated,
    approvedFunding: {
      amount: profile?.approvedFunding.amount,
      source: profile?.approvedFunding.source.id,
      conditionOfAllocation: profile?.approvedFunding.conditionOfAllocation,
    },
    additionalFunding: profile?.additionalFunding.map(
      ({ amount, source, conditionOfAllocation }) => ({
        amount,
        source: source.id,
        conditionOfAllocation,
      })
    ),
    originalStartDate: profile?.originalStartDate,
    originalCompletionDate: profile?.originalCompletionDate,
    actualStartDate: profile?.actualStartDate,
    actualCompletionDate: profile?.actualCompletionDate,
    deadlineOfUsingUpFunding: profile?.deadlineOfUsingUpFunding,
    proposal: convertFilesForInitialValues(profile?.proposal),
    supplements: convertFilesForInitialValues(profile?.supplements),
    historyLog: profile?.historyLog,
    historyLogByPMS: profile?.historyLogByPMS,
  };
};

const EditProfileForm = ({ data }) => {
  const [form] = AntdProForm.useForm();
  const { projectId } = useParams();
  const history = useHistory();

  const [updateProjectProfile, { loading: updateLoading }] = useMutation(
    UPDATE_PROJECT_PROFILE,
    {
      refetchQueries: [
        { query: GET_PROJECTS, variables: { ids: [projectId], state: 'ALL' } },
        {
          query: GET_PROJECT_PROFILE,
          variables: { ids: [projectId], status: 'ALL' },
        },
        {
          query: GET_LATEST_TERMINATION,
          variables: { projectId },
        },
      ],
      onCompleted: ({ updateProjectProfile }) => {
        message.success(`${updateProjectProfile.title} successfully edited`);
        history.push(`/projects/${projectId}/profile`);
      },
      onError: (err) => {
        console.log(err);
      },
    }
  );
  useLoading(updateLoading);

  return (
    <ProForm
      form={form}
      onFinish={(value) => {
        value.proposal = getId(value.proposal)[0];
        value.supplements = getId(value.supplements);
        value.accountSetUpDate = formatDatePickerValue(value.accountSetUpDate);
        value.accountClosedDate = formatDatePickerValue(
          value.accountClosedDate
        );
        value.originalStartDate = formatDatePickerValue(
          value.originalStartDate
        );
        value.originalCompletionDate = formatDatePickerValue(
          value.originalCompletionDate
        );
        value.actualStartDate = formatDatePickerValue(value.actualStartDate);
        value.actualCompletionDate = formatDatePickerValue(
          value.actualCompletionDate
        );
        value.deadlineOfUsingUpFunding = formatDatePickerValue(
          value.deadlineOfUsingUpFunding
        );
        value = fp.omit(['faculty'])(value);
        updateProjectProfile({
          variables: {
            input: {
              projectId,
              ...value,
            },
          },
        });
      }}
      initialValues={getInitialValues({ data })}
      scrollToFirstError={true}
    >
      <GeneralForm form={form} />
      <FundingForm />
      <ScheduleForm />
      <OtherForm form={form} />
    </ProForm>
  );
};

EditProfileForm.propTypes = {
  data: PropTypes.object.isRequired,
};

export default EditProfileForm;
